import { useState, useEffect } from 'react';
import { 
  isAuthTokenValid, 
  logoutAndRedirect,
  getUsername,
  getUserEmail,
  getSubjectId,
  getUserGroups,
  getCognitoAuthUrl
} from '../utils/auth/AuthService';
import SessionManager from '../utils/auth/SessionManager';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check if we're on the auth callback page
      if (window.location.pathname === '/auth') {
        // Let the Auth component handle this, but set loading to false
        setLoading(false);
        return;
      }

      try {
        const valid = await isAuthTokenValid();
        
        if (valid) {
          // User is authenticated, get user info
          const username = getUsername();
          const email = getUserEmail();
          const sub = getSubjectId();
          const groups = getUserGroups();
          
          setUser({
            name: username,
            email: email,
            sub: sub,
            groups: groups,
            isAdmin: groups.includes('admin'),
          });
          setIsAuthenticated(true);
        } else {
          // Not authenticated, save current URL and redirect to login
          const originPath = window.location.pathname === '/' ? '/dashboard' : window.location.pathname;
          SessionManager.setOriginUrl(originPath);
          
          // Only redirect if not already on auth page
          if (window.location.pathname !== '/auth') {
            window.location.replace(getCognitoAuthUrl());
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        const originPath = window.location.pathname === '/' ? '/dashboard' : window.location.pathname;
        SessionManager.setOriginUrl(originPath);
        
        // Only redirect if not already on auth page
        if (window.location.pathname !== '/auth') {
          window.location.replace(getCognitoAuthUrl());
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = () => {
    // If on root path, save /dashboard instead to avoid redirect loops
    const originPath = window.location.pathname === '/' ? '/dashboard' : window.location.pathname;
    SessionManager.setOriginUrl(originPath);
    window.location.replace(getCognitoAuthUrl());
  };

  const logout = () => {
    logoutAndRedirect();
  };

  const getAccessToken = () => {
    return SessionManager.getAccessToken();
  };

  const getIdToken = () => {
    return SessionManager.getIdToken();
  };

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    getAccessToken,
    getIdToken
  };
};
